import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Main page section matching other band pages
export const PageSection = styled.div`
  display: block;
`;

// Header section
export const HeaderSection = styled.div`
  margin-bottom: 2rem;
`;

export const HeaderTitle = styled.h1`
  color: #ff6b35;
  font-size: 1.8rem;
  font-weight: bold;
`;

export const HeaderSubtitle = styled.p`
  color: #cccccc;
  font-size: 1rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Content grid matching dashboard layout
export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

// Base card styles matching band dashboard
export const BaseCard = styled.div`
  background: rgba(26, 26, 26, 0.9);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff6b35;
    box-shadow: 0 5px 20px rgba(255, 107, 53, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const InfoCard = styled(BaseCard)`
  grid-column: span 1;
`;

export const FormCard = styled(BaseCard)`
  grid-column: span 1;
`;

export const VerificationCard = styled(BaseCard)`
  grid-column: span 1;
`;

// Section headers
export const InfoSection = styled.div`
  height: 100%;
`;

export const FormSection = styled.div`
  height: 100%;
`;

export const InfoHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #333;
`;

export const FormHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #333;
`;

export const VerificationHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #333;
`;

export const InfoTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 1rem;
  }
`;

export const FormTitle = styled(InfoTitle)``;

// Info grid and items
export const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

export const InfoLabel = styled.div`
  color: #cccccc;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;

  svg {
    color: #ff6b35;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

export const InfoValue = styled.div`
  color: #ffffff;
  font-weight: 500;
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

// Form styles
export const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  color: #cccccc;
  font-weight: 500;
  font-size: 0.9rem;
`;

export const InputWrapper = styled.div`
  position: relative;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid #555;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
    background: rgba(0, 0, 0, 0.5);
  }

  &:disabled {
    background: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: #888;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6b35;
  }

  svg {
    font-size: 1rem;
  }
`;

export const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35, #f94c10);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #f94c10, #e63946);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.7;
  }

  svg {
    font-size: 0.9rem;
  }
`;

// Verification styles
export const VerificationStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

export const VerificationBadge = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(props) =>
    props.$verified
      ? `
      background: rgba(34, 197, 94, 0.2);
      color: #22c55e;
      border: 1px solid #22c55e;
    `
      : `
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
      border: 1px solid #ef4444;
    `}

  svg {
    font-size: 1.25rem;
  }

  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;

    svg {
      font-size: 1rem;
    }
  }
`;

export const VerificationText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;

  strong {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
  }

  span {
    color: #cccccc;
    font-size: 0.9rem;
  }
`;

export const VerificationButton = styled(Button)`
  align-self: flex-start;

  @media (max-width: 768px) {
    align-self: stretch;
  }
`;

// Alert styles
export const Alert = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  margin-top: 1rem;
  animation: ${fadeIn} 0.3s ease-out;

  ${(props) =>
    props.$type === "success" &&
    `
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.3);
  `}

  ${(props) =>
    props.$type === "error" &&
    `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  `}

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

// Loading spinner
export const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;
